import { CsvErrorFilledIcon, CsvFilledIcon, DownloadIcon, UploadIcon } from '@app/assets';
import { Button, Container, IconButton, Typography } from '@app/components';
import { FileDropZoneWrapper } from '@app/components/file-drop-zone-wrapper/FileDropZoneWrapper';
import { clsxm } from '@app/styles/clsxm';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Close } from '@mui/icons-material';
import { friendlyByteSize } from '@app/utils/file.utils';
import { toast } from 'react-toastify';
import { CircularProgress, LinearProgress } from '@mui/material';
import { UpgradeBanner } from '@app/domain/plan/components/UpgradeBanner';
import { EPlanLimitType } from '@app/domain/plan/components/plan-limit-dialog/PlanLimitDialog';
import { usePlanLimits } from '@app/domain/plan/hooks/usePlanLimits';
import { ECodeType } from '@app/swagger-types';
import { ACCEPT, BULK_TYPE_ITEMS, MAX_FILE_SIZE_MB } from './constants';
import { QRCodeTypeSelector } from './QRCodeTypeSelector';
import { CsvParseResult, downloadSampleFile, handleFileDrop } from './file-utils';
import { useHandler } from '@app/hooks/useHandler.hook';
import { useFormContext } from 'react-hook-form';
import { QRCodeFormSchema } from '@app/domain/qr-code/api/qr-code.form.schema.api';

export const QRCodeBulk: React.FC<{
  onCancel: () => void;
  fileData: CsvParseResult;
  setFileData: (fileData: CsvParseResult) => void;
  forWidgetMode?: boolean;
}> = ({ forWidgetMode, setFileData, fileData }) => {
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext<QRCodeFormSchema>();
  const { canCreateBulk } = usePlanLimits();
  const [selectBulkType, setSelectBulkType] = useState(ECodeType.WEBSITE);

  const isWebsiteType = selectBulkType === ECodeType.WEBSITE;
  const isBarcodeType = selectBulkType === ECodeType.GS1;

  const [file, setFile] = useState<File | null>(fileData.file || null);

  const onFileDrop = useHandler(async (file: File) => {
    const qrValues = getValues();
    const { data, type, error } = await handleFileDrop(file, selectBulkType, qrValues);
    setFileData({
      file,
      data,
      type,
      error,
    });
  });

  const setSelectBulkTypeToForm = useHandler((type: ECodeType) => {
    setValue('codeType', type);
    setSelectBulkType(type);
  });

  const fileDto = file ? { fileName: file.name } : null;
  const fileId = file?.name;

  const renderContent = () => {
    if (!fileId) {
      return (
        <FileDropZoneWrapper
          accept={ACCEPT}
          className={clsxm(
            'flex cursor-pointer items-center justify-center gap-1 rounded-xl border border-gray-200 bg-gray-100 p-2 py-6 text-gray-800',
            'hover:bg-gray-100'
          )}
          onChange={(f) => {
            if (f.size > MAX_FILE_SIZE_MB) {
              toast.warn(t('error.maxFileSize'));
              return;
            }
            if (f.type !== ACCEPT) {
              toast.warn(t('error.onlyPdfFile'));
              return;
            }
            setFile(f);
            onFileDrop(f);
            // mutate(f);
          }}
        >
          <IconButton variant="transparent">
            <UploadIcon />
          </IconButton>
          {t('qr.bulk.dragCSVOr')}
          <span className="font-bold text-accept-main">{t('qr.bulk.browse')}</span>
        </FileDropZoneWrapper>
      );
    }

    return (
      <div
        className={clsxm(
          'flex items-center justify-between gap-1 rounded-xl border border-gray-200 bg-gray-100 p-2 py-4 text-gray-500'
        )}
      >
        <div className="flex gap-1">
          <CsvFilledIcon />
          {fileDto && (
            <div className="flex flex-col">
              <div className="text-xs text-gray-800">{fileDto.fileName}</div>
              {/* TODO show size when API ready */}
              {/* <div className="text-xs">{friendlyByteSize(fileDto.size)}</div> */}
              {file && file.name === fileDto.fileName && <div className="text-xs">{friendlyByteSize(file.size)}</div>}
            </div>
          )}
          {!fileDto && <CircularProgress />}
        </div>
        <IconButton
          variant="transparent"
          onClick={() => {
            setFile(null);
          }}
        >
          <Close />
        </IconButton>
      </div>
    );
  };

  const isError = Boolean(fileData.error);
  const isSuccess = !isError && file;

  useEffect(() => {
    if (file) {
      onFileDrop(file);
    }
  }, [selectBulkType, file, onFileDrop]);

  useEffect(() => {
    const type = getValues('codeType');
    setSelectBulkType(type);
  }, [selectBulkType, setSelectBulkTypeToForm, getValues]);

  const renderUploadingSection = () => {
    if (file) {
      return (
        <div
          className={clsxm(
            'flex flex-col justify-center items-center gap-3 p-3 border rounded-lg',
            isSuccess && 'bg-accept-light border-accept-medium',
            isError && 'bg-red-100 border-red-300',
            (isSuccess || isError) && 'pb-4'
          )}
        >
          <div className="flex gap-3 p-4">
            <div className="flex">{!isError ? <CsvFilledIcon /> : <CsvErrorFilledIcon />}</div>

            <div className="flex flex-col justify-between gap-2">
              <div className="flex justify-between gap-12">
                <Typography variant="xs" className="text-gray-800">
                  {t('qr.bulk.uploadingDocuments')}
                </Typography>

                <Typography variant="xs" className="text-gray-800 font-medium">
                  {'100%'}
                </Typography>
              </div>
              <LinearProgress
                className={clsxm(isError && 'text-red-500')}
                classes={{
                  root: clsxm('rounded h-[6px] bg-accept-menu-item', 'bg-white h-1 mb-1'),
                  bar: clsxm('rounded'),
                }}
                variant="determinate"
                color={isError ? 'inherit' : 'primary'}
                value={100}
              />
            </div>
          </div>

          {!isError && (
            <>
              <Typography variant="xs" className="text-center text-gray-800">
                <Trans i18nKey="qr.bulk.successDetected" values={{ qrAmount: fileData.data?.length || 0 }}>
                  <b />
                </Trans>
              </Typography>
              <Typography variant="xs" className="text-center text-gray-800">
                <Trans i18nKey="qr.bulk.generatedNext">
                  <b />
                </Trans>
              </Typography>
            </>
          )}
          {isError && (
            <>
              <Typography
                variant="xs"
                className="flex-col items-start text-left md:text-center md:flex-row md:items-start flex gap-1 text-gray-800"
              >
                {`${t('error.errorKeyWord')}!`}{' '}
                <span className="flex flex-col font-medium items-start">
                  {fileData.error?.split('\n').map((line, i) => (
                    <span key={i}>{line}</span>
                  ))}
                </span>
              </Typography>
              <Typography variant="xs" className="text-center text-gray-800 font-medium">
                {t('qr.bulk.uploadinErrorWarning')}
              </Typography>
            </>
          )}
        </div>
      );
    }
  };

  return (
    <div className="flex flex-col gap-3 p-0">
      {!canCreateBulk && <UpgradeBanner planLimitDialogType={EPlanLimitType.CREATE_BULK} />}
      <Container className="flex flex-col gap-6 xl:rounded-xl">
        <div className="flex flex-col gap-3">
          <Typography variant="l" className="font-bold text-lg">
            1. {t('qr.bulk.selectTypeTitle')}
          </Typography>
          <Typography variant="xxs" className="text-gray-600">
            {t('qr.bulk.selectTypeDescription')}
          </Typography>
        </div>

        <div className="flex flex-col gap-6">
          <Typography variant="l" className="font-bold text-lg">
            {t('qr.bulk.typeLabel')}
          </Typography>

          <div className=".testid-select-qr-code-type-container grid grid-cols-1 gap-3 sm:grid-cols-2">
            {BULK_TYPE_ITEMS.map((e) => (
              <QRCodeTypeSelector
                key={e.value}
                itemType={e}
                hideOnMobile={forWidgetMode}
                showExpandArrow={true}
                selectBulkType={selectBulkType}
                setSelectBulkType={setSelectBulkTypeToForm}
              />
            ))}
            {/* TODO handle other code types for mobile */}
            {/* <SwipeableDrawer
              anchor={'bottom'}
              open={isOpenDrawer}
              disableSwipeToOpen
              onOpen={() => setIsOpenDrawer(true)}
              onClose={() => setIsOpenDrawer(false)}
              classes={{
                paper: 'bg-transparent',
              }}
            >
              <div className="rounded-t-2xl flex flex-col gap-4 p-4 py-8 bg-white">
                {BULK_TYPE_ITEMS.map((e) => renderTypeItem({ itemType: e }))}
              </div>
            </SwipeableDrawer> */}
          </div>
        </div>

        <div className="flex flex-col gap-6">
          <Typography variant="l" className="font-bold text-lg">
            {t('qr.bulk.downloadUploadFormat')}
          </Typography>
          <div>
            <Button
              onClick={() => {
                downloadSampleFile(selectBulkType);
              }}
              variant="outlined"
              // disabled={isLoading}
              startIcon={<DownloadIcon />}
            >
              {isWebsiteType && t('qr.bulk.downloadWebsiteCsv')}
              {isBarcodeType && t('qr.bulk.downloadBarcodeCsv')}
            </Button>
          </div>
        </div>
      </Container>
      <Container className="flex flex-col gap-6 xl:rounded-xl">
        <div className="flex flex-col gap-3">
          <Typography variant="l" className="font-bold text-lg">
            2. {t('qr.bulk.uploadCsvTitle')}
          </Typography>
          <Typography variant="xxs" className="text-gray-600">
            {t('qr.bulk.uploadCsvDescription')}
          </Typography>
        </div>
        {renderContent()}
        {renderUploadingSection()}
      </Container>
    </div>
  );
};
